import { apiClient } from './api'

// ========================================
// Types & Interfaces
// ========================================

export type BackupSchedule = {
  id: string
  name: string
  description?: string
  vm_id: string
  customer_id: string
  backup_product_id: string
  datastore_id: string
  cron_expression: string
  timezone: string
  backup_type: string
  compression_level: string
  encryption_enabled: boolean
  verification_enabled: boolean
  keep_last: number
  keep_daily: number
  keep_weekly: number
  keep_monthly: number
  keep_yearly?: number
  notify_on_success: boolean
  notify_on_failure: boolean
  notification_emails: string[]
  status: string
  last_run_at?: string
  last_run_status?: string
  next_run_at?: string
  consecutive_failures: number
  created_by: string
  created_at: string
  updated_at: string
}

export type BackupSnapshot = {
  id: string
  name: string
  description?: string
  backup_id: string
  backup_time: string
  backup_type: string
  size_bytes?: number
  deduplicated_size_bytes?: number
  compression_ratio?: number
  status: string
  progress_percentage: number
  error_message?: string
  started_at?: string
  completed_at?: string
  duration_seconds?: number
  is_verified: boolean
  verified_at?: string
  verification_status?: string
  checksum?: string
  is_encrypted: boolean
  encryption_fingerprint?: string
  protected: boolean
  expires_at?: string
  tags: string[]
  notes?: string
  vm_id: string
  vm_name?: string
  vm_storage_gb?: number
  customer_id: string
  schedule_id?: string
  job_id?: string
  datastore_id: string
  created_by: string
  created_at: string
  updated_at: string
}

export type BackupRestoreJob = {
  id: string
  snapshot_id: string
  customer_id: string
  restore_type: string
  target_vm_id?: string
  new_vm_name?: string
  target_datastore?: string
  start_after_restore: boolean
  replace_existing: boolean
  restore_network_config: boolean
  status: string
  progress_percentage: number
  error_message?: string
  started_at?: string
  completed_at?: string
  duration_seconds?: number
  initiated_reason?: string
  initiated_by: string
  created_at: string
  updated_at: string
}

export type BackupUsage = {
  snapshot_id: string
  customer_id: string
  size_bytes?: number
  deduplicated_size_bytes?: number
  size_gb: number
  cost: number
  currency: string
  backup_date: string
}

// ========================================
// Backup Jobs (Veeam-style)
// ========================================

export type BackupJobVM = {
  id: string
  vm_id: string
  vm_name?: string
  vm_status?: string
  /** VM que já não existe para o cliente (excluída ou em soft delete) — régua do backend.
   *  Opcional porque o backend que a expõe pode ainda não ter deployado: quem lê deve cair
   *  no fallback por `vm_status` em vez de tratar `undefined` como "VM viva". */
  vm_removed?: boolean
  priority: number
  is_active: boolean
  added_at: string
  removed_at?: string
  snapshot_count: number
  storage_used_bytes: number
}

export type BackupJob = {
  id: string
  name: string
  description?: string
  customer_id: string
  backup_product_id: string
  datastore_id: string
  cron_expression: string
  timezone: string
  backup_type: string
  compression_level: string
  encryption_enabled: boolean
  verification_enabled: boolean
  keep_last: number
  keep_daily: number
  keep_weekly: number
  keep_monthly: number
  keep_yearly?: number
  stop_on_failure: boolean
  notify_on_success: boolean
  notify_on_failure: boolean
  notification_emails: string[]
  status: string
  last_run_at?: string
  last_run_status?: string
  next_run_at?: string
  consecutive_failures: number
  created_by: string
  created_at: string
  updated_at: string
  vms?: BackupJobVM[]
}

export type CreateBackupJobRequest = {
  name: string
  description?: string
  cron_expression: string
  timezone?: string
  backup_type?: string
  compression_level?: string
  encryption_enabled?: boolean
  verification_enabled?: boolean
  keep_last?: number
  keep_daily?: number
  keep_weekly?: number
  keep_monthly?: number
  keep_yearly?: number
  stop_on_failure?: boolean
  notify_on_failure?: boolean
  notification_emails?: string[]
  vm_ids?: string[]
}

export type UpdateBackupJobRequest = {
  name?: string
  description?: string
  cron_expression?: string
  timezone?: string
  backup_type?: string
  compression_level?: string
  encryption_enabled?: boolean
  verification_enabled?: boolean
  keep_last?: number
  keep_daily?: number
  keep_weekly?: number
  keep_monthly?: number
  keep_yearly?: number
  stop_on_failure?: boolean
  notify_on_success?: boolean
  notify_on_failure?: boolean
  notification_emails?: string[]
  status?: string
  vm_ids?: string[]
}

export type AddVMsToJobRequest = {
  vm_ids: string[]
}

export type ReorderJobVMsRequest = {
  vm_ids: string[]
}

// ========================================
// Admin - Backup Analytics Types
// ========================================

export type GlobalBackupUsageSummary = {
  billing_period: string // YYYY-MM
  total_storage_gb: number
  total_cost: number
  total_customers: number
  total_resellers: number
  total_vms_with_backup: number
  currency: string
  growth_percentage?: number // Crescimento vs mês anterior
}

export type CustomerBackupUsageItem = {
  customer_id: string
  customer_name: string
  reseller_id: string
  reseller_name: string
  total_storage_gb: number
  total_cost: number
  vms_with_backup: number
  billing_period: string
}

export type ResellerBackupUsageItem = {
  reseller_id: string
  reseller_name: string
  total_storage_gb: number
  total_cost: number
  total_customers: number
  vms_with_backup: number
  billing_period: string
}

export type BackupUsageTrendItem = {
  billing_period: string // YYYY-MM
  total_storage_gb: number
  total_cost: number
  total_vms: number
  total_customers: number
}

export type BackupUsageTrends = {
  trends: BackupUsageTrendItem[]
  months: number
  currency: string
}

// ========================================
// Request Schemas
// ========================================

export type CreateBackupScheduleRequest = {
  vm_id: string
  backup_product_id: string
  datastore_id: string
  name: string
  cron_expression: string
  timezone?: string
  backup_type?: string
  compression_level?: string
  encryption_enabled?: boolean
  verification_enabled?: boolean
  keep_last?: number
  keep_daily?: number
  keep_weekly?: number
  keep_monthly?: number
  keep_yearly?: number
  notify_on_failure?: boolean
  notification_emails?: string[]
  description?: string
}

export type UpdateBackupScheduleRequest = {
  name?: string
  cron_expression?: string
  timezone?: string
  backup_type?: string
  compression_level?: string
  encryption_enabled?: boolean
  verification_enabled?: boolean
  keep_last?: number
  keep_daily?: number
  keep_weekly?: number
  keep_monthly?: number
  keep_yearly?: number
  notify_on_success?: boolean
  notify_on_failure?: boolean
  notification_emails?: string[]
  description?: string
}

export type InitiateRestoreRequest = {
  snapshot_id: string
  restore_type: string
  target_vm_id?: string
  new_vm_name?: string
  target_datastore?: string
  start_after_restore?: boolean
  replace_existing?: boolean
  restore_network_config?: boolean
  initiated_reason?: string
}

export type BatchBackupRequest = {
  schedule_ids: string[]
  manual?: boolean
}

export type BatchDeleteSnapshotsRequest = {
  snapshot_ids: string[]
  force?: boolean
}

export type PruneBackupsResponse = {
  schedule_id: string
  schedule_name: string
  pruned_count: number
  message: string
}

export type BatchBackupResponse = {
  total: number
  successful: number
  failed: number
  snapshots: BackupSnapshot[]
  errors: {
    schedule_id: string
    schedule_name: string
    error: string
  }[]
}

export type BatchDeleteSnapshotsResponse = {
  total: number
  deleted: number
  failed: number
  errors: {
    snapshot_id: string
    snapshot_name: string
    error: string
  }[]
}

export type BackupActivityLog = {
  id: string
  customer_id: string
  vm_id?: string
  schedule_id?: string
  snapshot_id?: string
  event_type: string
  status: string
  snapshot_name?: string
  vm_name?: string
  details?: Record<string, unknown>
  created_at: string
}

// ========================================
// API Client - Backup Schedules
// ========================================

export const backupsApi = {
  // ========================================
  // Backup Schedules
  // ========================================

  /**
   * Lista VMs do customer para criar agendamento
   */
  listCustomerVMs: (): Promise<
    {
      id: string
      name: string
      status: string
      cluster_id: string | null
    }[]
  > => {
    return apiClient.get('/api/v1/backups/vms')
  },

  /**
   * Lista schedules de backup
   */
  listSchedules: (params?: {
    vm_id?: string
    status?: string
    skip?: number
    limit?: number
  }): Promise<BackupSchedule[]> => {
    return apiClient.get('/api/v1/backups/schedules', { params })
  },

  /**
   * Cria schedule de backup
   */
  createSchedule: (data: CreateBackupScheduleRequest): Promise<BackupSchedule> => {
    return apiClient.post('/api/v1/backups/schedules', data)
  },

  /**
   * Obtém detalhes de schedule
   */
  getSchedule: (scheduleId: string): Promise<BackupSchedule> => {
    return apiClient.get(`/api/v1/backups/schedules/${scheduleId}`)
  },

  /**
   * Atualiza schedule de backup
   */
  updateSchedule: (
    scheduleId: string,
    data: UpdateBackupScheduleRequest,
  ): Promise<BackupSchedule> => {
    return apiClient.put(`/api/v1/backups/schedules/${scheduleId}`, data)
  },

  /**
   * Deleta (desabilita) schedule de backup
   */
  deleteSchedule: (scheduleId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/backups/schedules/${scheduleId}`)
  },

  /**
   * Executa backup manual a partir de schedule
   */
  executeManualBackup: (scheduleId: string): Promise<BackupSnapshot> => {
    return apiClient.post(`/api/v1/backups/schedules/${scheduleId}/execute`)
  },

  /**
   * Faz prune de backups antigos
   */
  pruneOldBackups: (scheduleId: string): Promise<PruneBackupsResponse> => {
    return apiClient.post(`/api/v1/backups/schedules/${scheduleId}/prune`)
  },

  // ========================================
  // Backup Jobs (Veeam-style)
  // ========================================

  listJobs: (params?: { status?: string; skip?: number; limit?: number }): Promise<BackupJob[]> => {
    return apiClient.get('/api/v1/backups/jobs', { params })
  },

  createJob: (data: CreateBackupJobRequest): Promise<BackupJob> => {
    return apiClient.post('/api/v1/backups/jobs', data)
  },

  getJob: (jobId: string): Promise<BackupJob> => {
    return apiClient.get(`/api/v1/backups/jobs/${jobId}`)
  },

  updateJob: (jobId: string, data: UpdateBackupJobRequest): Promise<BackupJob> => {
    return apiClient.put(`/api/v1/backups/jobs/${jobId}`, data)
  },

  deleteJob: (jobId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/backups/jobs/${jobId}`)
  },

  addVMsToJob: (jobId: string, data: AddVMsToJobRequest): Promise<BackupJob> => {
    return apiClient.post(`/api/v1/backups/jobs/${jobId}/vms`, data)
  },

  reorderJobVMs: (jobId: string, data: ReorderJobVMsRequest): Promise<BackupJob> => {
    return apiClient.put(`/api/v1/backups/jobs/${jobId}/vms/reorder`, data)
  },

  removeVMFromJob: (jobId: string, vmId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/backups/jobs/${jobId}/vms/${vmId}`)
  },

  executeJob: (jobId: string, vmId?: string): Promise<BackupSnapshot[]> => {
    const params = vmId ? { vm_id: vmId } : undefined
    return apiClient.post(`/api/v1/backups/jobs/${jobId}/execute`, null, { params })
  },

  // ========================================
  // Backup Snapshots
  // ========================================

  /**
   * Lista snapshots de backup
   */
  listSnapshots: (params?: {
    vm_id?: string
    schedule_id?: string
    status?: string
    date_from?: string
    date_to?: string
    skip?: number
    limit?: number
  }): Promise<BackupSnapshot[]> => {
    return apiClient.get('/api/v1/backups/snapshots', { params })
  },

  /**
   * Obtém detalhes de snapshot
   */
  getSnapshot: (snapshotId: string): Promise<BackupSnapshot> => {
    return apiClient.get(`/api/v1/backups/snapshots/${snapshotId}`)
  },

  /**
   * Deleta snapshot de backup
   */
  deleteSnapshot: (snapshotId: string, force?: boolean): Promise<void> => {
    const query = force ? '?force=true' : ''
    return apiClient.delete(`/api/v1/backups/snapshots/${snapshotId}${query}`)
  },

  /**
   * Verifica integridade do backup
   */
  verifySnapshot: (snapshotId: string): Promise<BackupSnapshot> => {
    return apiClient.post(`/api/v1/backups/snapshots/${snapshotId}/verify`)
  },

  /**
   * Obtém informações de uso e custo do backup
   */
  getSnapshotUsage: (snapshotId: string): Promise<BackupUsage> => {
    return apiClient.get(`/api/v1/backups/snapshots/${snapshotId}/usage`)
  },

  // ========================================
  // Restore Operations
  // ========================================

  /**
   * Lista jobs de restore
   */
  listRestoreJobs: (params?: {
    snapshot_id?: string
    status?: string
    skip?: number
    limit?: number
  }): Promise<BackupRestoreJob[]> => {
    return apiClient.get('/api/v1/backups/restores', { params })
  },

  /**
   * Inicia operação de restore
   */
  initiateRestore: (data: InitiateRestoreRequest): Promise<BackupRestoreJob> => {
    return apiClient.post('/api/v1/backups/restores', data)
  },

  /**
   * Obtém detalhes de restore job
   */
  getRestoreJob: (restoreId: string): Promise<BackupRestoreJob> => {
    return apiClient.get(`/api/v1/backups/restores/${restoreId}`)
  },

  /**
   * Cancela restore job em execução
   */
  cancelRestoreJob: (restoreId: string): Promise<BackupRestoreJob> => {
    return apiClient.post(`/api/v1/backups/restores/${restoreId}/cancel`)
  },

  // ========================================
  // Activity Logs
  // ========================================

  listActivityLogs: (params?: {
    vm_id?: string
    schedule_id?: string
    event_type?: string
    limit?: number
  }): Promise<BackupActivityLog[]> => {
    return apiClient.get('/api/v1/backups/activity-logs', { params })
  },

  // ========================================
  // Batch Operations
  // ========================================

  /**
   * Executa múltiplos backups de uma vez
   */
  batchExecuteBackups: (data: BatchBackupRequest): Promise<BatchBackupResponse> => {
    return apiClient.post('/api/v1/backups/batch/execute', data)
  },

  /**
   * Deleta múltiplos snapshots de uma vez
   */
  batchDeleteSnapshots: (
    data: BatchDeleteSnapshotsRequest,
  ): Promise<BatchDeleteSnapshotsResponse> => {
    return apiClient.post('/api/v1/backups/batch/delete-snapshots', data)
  },

  // ========================================
  // Admin - Backup Analytics (Cloud Provider)
  // ========================================

  /**
   * Obtém resumo global de uso de backup (Cloud Provider)
   */
  getGlobalBackupUsageSummary: (params?: {
    billing_period?: string // YYYY-MM
  }): Promise<GlobalBackupUsageSummary> => {
    return apiClient.get('/api/v1/backup-usage/admin/global-summary', { params })
  },

  /**
   * Obtém uso de backup por reseller (Cloud Provider)
   */
  getBackupUsageByReseller: (params?: {
    billing_period?: string
    limit?: number
  }): Promise<ResellerBackupUsageItem[]> => {
    return apiClient.get('/api/v1/backup-usage/admin/by-reseller', { params })
  },

  /**
   * Obtém uso de backup por customer (Cloud Provider)
   */
  getBackupUsageByCustomer: (params?: {
    billing_period?: string
    reseller_id?: string
    limit?: number
  }): Promise<CustomerBackupUsageItem[]> => {
    return apiClient.get('/api/v1/backup-usage/admin/by-customer', { params })
  },

  /**
   * Obtém tendências de uso de backup (Cloud Provider)
   */
  getBackupUsageTrends: (params?: { months?: number }): Promise<BackupUsageTrends> => {
    return apiClient.get('/api/v1/backup-usage/admin/trends', { params })
  },

  // ========================================
  // Reseller - Backup Analytics
  // ========================================

  /**
   * Obtém resumo de uso de backup do reseller (agregado de todos os customers)
   */
  getResellerBackupUsageSummary: (params?: {
    billing_period?: string
  }): Promise<GlobalBackupUsageSummary> => {
    return apiClient.get('/api/v1/backup-usage/reseller/summary', { params })
  },

  /**
   * Obtém uso de backup por customer do reseller
   */
  getResellerBackupUsageByCustomer: (params?: {
    billing_period?: string
    limit?: number
  }): Promise<CustomerBackupUsageItem[]> => {
    return apiClient.get('/api/v1/backup-usage/reseller/by-customer', { params })
  },

  /**
   * Obtém tendências de uso de backup do reseller
   */
  getResellerBackupUsageTrends: (params?: { months?: number }): Promise<BackupUsageTrends> => {
    return apiClient.get('/api/v1/backup-usage/reseller/trends', { params })
  },

  // ========================================
  // Export Functions
  // ========================================

  /**
   * Exporta dados de uso de backup em formato CSV
   */
  exportBackupUsageCSV: (params: { billing_period: string; customer_id?: string }): string => {
    const query = new URLSearchParams({
      billing_period: params.billing_period,
      ...(params.customer_id && { customer_id: params.customer_id }),
    })
    return `/api/v1/backup-usage/export/csv?${query.toString()}`
  },

  /**
   * Exporta dados de uso de backup em formato Excel
   */
  exportBackupUsageExcel: (params: { billing_period: string; customer_id?: string }): string => {
    const query = new URLSearchParams({
      billing_period: params.billing_period,
      ...(params.customer_id && { customer_id: params.customer_id }),
    })
    return `/api/v1/backup-usage/export/excel?${query.toString()}`
  },

  /**
   * Exporta dados de uso de backup em formato PDF
   */
  exportBackupUsagePDF: (params: { billing_period: string; customer_id?: string }): string => {
    const query = new URLSearchParams({
      billing_period: params.billing_period,
      ...(params.customer_id && { customer_id: params.customer_id }),
    })
    return `/api/v1/backup-usage/export/pdf?${query.toString()}`
  },

  /**
   * Baixa o export de uso de backup no formato escolhido, já autenticado.
   *
   * Usa o apiClient (axios) para herdar o interceptor de token e o baseURL —
   * o fetch manual anterior lia o token de uma chave inexistente (`auth_token`)
   * e montava a URL com `import.meta.env.VITE_API_URL` (undefined em dev),
   * baixando o corpo de erro (JSON) em vez do arquivo, qualquer que fosse o
   * formato. Retorna o Blob e o nome de arquivo (o mesmo `backup-usage-<periodo>.<ext>`
   * que o backend usa no Content-Disposition).
   */
  downloadBackupUsageExport: async (
    format: 'csv' | 'excel' | 'pdf',
    params: { billing_period: string; customer_id?: string },
  ): Promise<{ blob: Blob; filename: string }> => {
    const url =
      format === 'csv'
        ? backupsApi.exportBackupUsageCSV(params)
        : format === 'excel'
          ? backupsApi.exportBackupUsageExcel(params)
          : backupsApi.exportBackupUsagePDF(params)
    try {
      // getLong (timeout 120s) — exports de PDF/Excel de períodos grandes podem
      // demorar mais que o timeout padrão de 30s do apiClient.get.
      const blob = await apiClient.getLong<Blob>(url, { responseType: 'blob' })
      const ext = format === 'excel' ? 'xlsx' : format
      return { blob, filename: `backup-usage-${params.billing_period}.${ext}` }
    } catch (error) {
      // Com responseType:'blob', o axios entrega o corpo de erro como Blob —
      // reidrata o JSON ({error:{message}}) em response.data para que
      // getErrorMessage consiga extrair a mensagem real do backend.
      const data = (error as { response?: { data?: unknown } })?.response?.data
      if (data instanceof Blob) {
        try {
          ;(error as { response: { data: unknown } }).response.data = JSON.parse(await data.text())
        } catch {
          /* corpo não-JSON (ex.: HTML de 502) — mantém o Blob original */
        }
      }
      throw error
    }
  },
}

// ========================================
// Helper Functions
// ========================================

/**
 * Formata tamanho em bytes para formato legível
 */
export const formatBytes = (bytes?: number): string => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

/**
 * Calcula porcentagem de compressão
 */
export const calculateCompressionRatio = (
  originalSize?: number,
  compressedSize?: number,
): string => {
  if (!originalSize || !compressedSize) return '-'
  const ratio = ((1 - compressedSize / originalSize) * 100).toFixed(1)
  return `${ratio}%`
}

/**
 * Formata duração em segundos para formato legível
 */
export const formatDuration = (seconds?: number): string => {
  if (!seconds) return '-'
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${minutes}m`
}

/**
 * Obtém cor do status de backup
 */
export const getBackupStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    completed: 'text-green-600 bg-green-100',
    running: 'text-blue-600 bg-blue-100',
    pending: 'text-yellow-600 bg-yellow-100',
    failed: 'text-red-600 bg-red-100',
    verifying: 'text-purple-600 bg-purple-100',
    verified: 'text-green-600 bg-green-100',
    in_progress: 'text-blue-600 bg-blue-100',
  }
  return colors[status] || 'text-gray-600 bg-gray-100'
}

/**
 * Obtém label traduzido do status
 */
export const getBackupStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    completed: 'Concluído',
    running: 'Em execução',
    pending: 'Pendente',
    failed: 'Falhou',
    verifying: 'Verificando',
    verified: 'Verificado',
    in_progress: 'Em andamento',
    active: 'Ativo',
    paused: 'Pausado',
    disabled: 'Desabilitado',
    error: 'Erro',
    cancelled: 'Cancelado',
  }
  return labels[status] || status
}

/**
 * Obtém label de tipo de backup
 */
export const getBackupTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    manual: 'Manual',
    automatic: 'Automático',
    snapshot: 'Snapshot',
    full: 'Completo',
    incremental: 'Incremental',
    differential: 'Diferencial',
  }
  return labels[type] || type
}

/**
 * Obtém label de tipo de restore
 */
export const getRestoreTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    full: 'Completo',
    clone: 'Clone',
    partial: 'Parcial',
    'file-level': 'Nível de arquivo',
  }
  return labels[type] || type
}
